import { SITE } from '../lib/site';
import { LANGS } from '../i18n';

export const ORIGIN = SITE.url.replace('/dental', '');
export const ORG_ID = `${SITE.url}/#organization`;
export const WEBSITE_ID = `${SITE.url}/#website`;
export const personId = (slug) => `${SITE.url}/#person-${slug}`;
export const serviceId = (slug) => `${SITE.url}/treatments/${slug}#service`;

const SAME_AS = [
    'https://www.tiktok.com/@drirmaariandi',
    'https://www.instagram.com/drirmaariandi',
    'https://www.facebook.com/share/1JURZ614h6/',
];

const ADDRESS = {
    '@type': 'PostalAddress',
    streetAddress: '1-24 & 1-25, Aras 1, Plaza Larkin, Jalan Garuda',
    addressLocality: 'Johor Bahru',
    postalCode: '80350',
    addressRegion: 'Johor',
    addressCountry: 'MY',
};

const LANGUAGES = LANGS.map((l) => l.htmlLang);

export const TREATMENT_SERVICES = [
    { slug: 'conventional-braces', name: 'Conventional Braces' },
    { slug: 'clear-aligners', name: 'Clear Aligners' },
    { slug: 'frc', name: 'Fibre Reinforced Composite (FRC) Bridge' },
    { slug: 'zirconia-crowns-bridges', name: 'Zirconia Crowns & Bridges' },
    { slug: 'aesthetic-veneers', name: 'Aesthetic Veneers / Smile Makeover' },
];

export const clinicSchema = {
    '@context': 'https://schema.org',
    '@type': 'Dentist',
    '@id': ORG_ID,
    name: SITE.name,
    alternateName: 'Irma Ariandi Dental Clinic',
    slogan: SITE.tagline,
    description: 'Dental clinic in Plaza Larkin, Johor Bahru — conventional braces, clear aligners, fibre reinforced composite (FRC) bridges, zirconia crowns and bridges, aesthetic veneers and digital dentistry.',
    url: SITE.url,
    telephone: `+${SITE.whatsappIntl}`,
    email: SITE.email,
    image: `${ORIGIN}/img/og-default.jpg`,
    address: ADDRESS,
    openingHoursSpecification: [
        { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday'], opens: '10:00', closes: '13:00' },
        { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday'], opens: '14:00', closes: '18:00' },
        { '@type': 'OpeningHoursSpecification', dayOfWeek: 'Saturday', opens: '09:00', closes: '12:30' },
    ],
    availableLanguage: LANGUAGES,
    areaServed: ['Johor Bahru', 'Larkin', 'Johor', 'Malaysia', 'Singapore', 'Indonesia'],
    sameAs: SAME_AS,
    employee: [{ '@id': personId('dr-irma-ariandi') }, { '@id': personId('dr-ariandi') }],
    hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Dental services',
        itemListElement: [
            ...TREATMENT_SERVICES.map((s) => ({
                '@type': 'Offer',
                itemOffered: { '@type': 'Service', '@id': serviceId(s.slug), name: s.name },
            })),
            {
                '@type': 'Offer',
                itemOffered: { '@type': 'Service', '@id': `${SITE.url}/digital-dentistry#service`, name: 'Digital Dentistry / Digital Dental Scanning' },
            },
        ],
    },
};

export const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    url: SITE.url,
    name: SITE.name,
    publisher: { '@id': ORG_ID },
    inLanguage: LANGUAGES,
};

export const dentistPersonSchema = (doctor) => ({
    '@context': 'https://schema.org',
    '@type': 'Person',
    '@id': personId(doctor.slug),
    name: doctor.name,
    jobTitle: 'Dentist',
    description: doctor.creds,
    image: `${ORIGIN}${doctor.photo}`,
    hasCredential: {
        '@type': 'EducationalOccupationalCredential',
        credentialCategory: 'degree',
        name: 'Doctor of Dental Surgery (DDS), Universiti Kebangsaan Malaysia (UKM)',
    },
    worksFor: { '@id': ORG_ID },
    sameAs: doctor.key === 'irma' ? SAME_AS : undefined,
});

export const serviceSchema = (slug, name, description, lang, path) => ({
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': serviceId(slug),
    name,
    description,
    serviceType: 'Dental treatment',
    provider: { '@id': ORG_ID },
    areaServed: ['Johor Bahru', 'Larkin', 'Johor'],
    availableLanguage: LANGUAGES,
    url: `${SITE.url}/${lang}${path || `/treatments/${slug}`}`,
});

export const breadcrumbSchema = (lang, items) => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: it.name,
        item: `${SITE.url}/${lang}${it.path === '/' ? '' : it.path}`,
    })),
});

const authorId = (author) =>
    /irma/i.test(author || '') ? personId('dr-irma-ariandi') : /ariandi/i.test(author || '') ? personId('dr-ariandi') : personId('dr-irma-ariandi');

export const articleSchema = ({ lang, title, description, datePublished, dateModified, slug, author, image }) => ({
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    '@id': `${SITE.url}/${lang}/kolum/${slug}#article`,
    headline: title,
    description: description,
    image: image ? [image.startsWith('http') ? image : `${ORIGIN}${image}`] : undefined,
    datePublished: datePublished || undefined,
    dateModified: dateModified || datePublished || undefined,
    inLanguage: (LANGS.find((l) => l.code === lang) || {}).htmlLang || 'ms-MY',
    author: { '@id': authorId(author) },
    publisher: { '@id': ORG_ID },
    isPartOf: { '@id': WEBSITE_ID },
    mainEntityOfPage: `${SITE.url}/${lang}/kolum/${slug}`,
});

export const faqSchema = (faqs) => ({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: (faqs || []).map((f) => ({
        '@type': 'Question',
        name: f.q || f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.a || f.answer },
    })),
});
